import {
  Text,
  Timeline,
  Collapse,
  Badge,
  Group,
  Center,
  Loader,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconHistory,
} from "@tabler/icons-react";
import { useState } from "react";
import { displayISODate } from "../utils/display";
import { intentionStatusData } from "../utils/consts";
import { usePaymentHistoryQuery } from "../api/queries";
import { usePaymentQuery } from "../api/queries";
import { InvoiceStatusMenu } from "./InvoiceStatusMenu";

type PaymentHistorySectionProps = {
  invoiceId: string;
};

export const PaymentHistorySection = ({
  invoiceId,
}: PaymentHistorySectionProps) => {
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const { data: paymentHistory = [], isLoading: isHistoryLoading } =
    usePaymentHistoryQuery(invoiceId, { enabled: Boolean(invoiceId) });
  const { data: payment } = usePaymentQuery(invoiceId, {
    enabled: Boolean(invoiceId),
  });

  const getStatusBackgroundColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      green: "bg-green-100",
      yellow: "bg-yellow-100",
      orange: "bg-orange-100",
      red: "bg-red-100",
    };
    const color =
      intentionStatusData[status as keyof typeof intentionStatusData]
        ?.tableIconColor;
    return colorMap[color] || "bg-gray-100";
  };

  return (
    <div className="w-full space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Status & history
      </h2>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setHistoryOpen(!historyOpen)}
        >
          <Group gap="xs">
            <IconHistory size={20} className="text-brand" />
            {isHistoryLoading ? (
              <Loader size="sm" />
            ) : (
              <Text className="text-base font-semibold text-brand">
                Email flow ({paymentHistory.length})
              </Text>
            )}
          </Group>
          <Group gap="xs" align="center">
            <div onClick={(e) => e.stopPropagation()}>
              <InvoiceStatusMenu
                currentStatus={payment?.intention_status ?? "Unresolved"}
                externalId={invoiceId}
              />
            </div>
            {historyOpen ? (
              <IconChevronUp size={20} className="text-brand" />
            ) : (
              <IconChevronDown size={20} className="text-brand" />
            )}
          </Group>
        </div>
      </div>

      <Collapse in={historyOpen} transitionDuration={200}>
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          {isHistoryLoading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : paymentHistory.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="md">
              No history entries yet.
            </Text>
          ) : (
            <Timeline bulletSize={34} lineWidth={2}>
              {paymentHistory.map((entry) => {
                return (
                  <Timeline.Item
                    key={entry.id}
                    bullet={
                      <IconHistory size={22} className="text-brand" />
                    }
                    title={
                      <Group gap="xs" align="center">
                        <Text className="font-semibold flex place-items-center text-gray-800 text-base">
                          <span
                            className={`${getStatusBackgroundColor(entry.current_status)} p-1 rounded-2xl`}
                          >
                            {entry.current_status.replaceAll("_", " ")}
                          </span>
                        </Text>
                        {entry.is_llm ? (
                          <Badge color="blue" size="sm" variant="light">
                            AI
                          </Badge>
                        ) : (
                          <Badge color="green" size="sm" variant="light">
                            Manual
                          </Badge>
                        )}
                      </Group>
                    }
                  >
                    <Text
                      c="dimmed"
                      size="xs"
                      mt={4}
                      mb={entry.description ? 8 : 0}
                      className="italic text-gray-500"
                    >
                      {displayISODate(entry.created_at)}
                      {entry.author_name && (
                        <> &nbsp; | &nbsp; Author: {entry.author_name}</>
                      )}
                    </Text>

                    {entry.description && (
                      <Text
                        size="sm"
                        mt={8}
                        className="text-gray-700"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {entry.description}
                      </Text>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}
        </div>
      </Collapse>
    </div>
  );
};
